import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  getLockSequencePoolingInfo,
  putLockSequenceTagPooling,
  putLockSequenceUntagPooling,
} from 'features/Order/services/lockSequencePooling'
import { callErrorMsg, getErrorMessage } from 'helpers/errorMsg'
import {
  getPoolDataAPI,
  tagPoolDataAPI,
  unTagPoolDataAPI,
  fetchUnpoolReasonsAPI,
} from 'utils/apiList/orderPool'
import { logOpenTelemetryApiError } from 'utils/helpers/openTelemetryLog'

const TAG_POOL_ACTION = 'tag order for pooling'
const UNTAG_POOL_ACTION = 'untag order for pooling'
const PAGE_ATTRIBUTE = 'app.page'
const ORDER_ID_ATTRIBUTE = 'order_id'

const getTargetOrderIds = (payload, action) => {
  const orderIds =
    action === TAG_POOL_ACTION
      ? payload.order_ids_pool?.orders || []
      : (payload.data || []).flatMap((item) => item.order_ids_unpool || [])

  return [...new Set(orderIds)].join(',')
}

const logPoolingApiError = ({ action, error, otelContext, payload }) => {
  if (!otelContext?.page) {
    return
  }

  logOpenTelemetryApiError({
    action,
    attributes: {
      [ORDER_ID_ATTRIBUTE]: getTargetOrderIds(payload, action),
      [PAGE_ATTRIBUTE]: otelContext.page,
    },
    domain: 'web-admin',
    error,
    message: getErrorMessage(error),
  })
}

const initialState = {
  pools: [],
  isLoading: false,
  mainPoolData: [],
}

const orderPooling = createSlice({
  name: 'orderPooling',
  initialState,
  reducers: {
    resetPool: () => initialState,
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setPools: (state, action) => {
      const { pools } = state
      const index = pools.findIndex((each) => each.order_invoice === action.payload.order_invoice)
      if (index == -1) {
        state.pools.push(action.payload)
      } else {
        state.pools = pools.filter((each) => each.order_invoice !== action.payload.order_invoice)
      }
    },
    setMainPoolData: (state, action) => {
      state.mainPoolData = action.payload
    },
  },
})

export const { resetPool, setLoading, setPools, setMainPoolData } = orderPooling.actions

export const fetchPoolData = createAsyncThunk(
  'orderPooling/fetchPoolData',
  async (payload, { dispatch, getState }) => {
    dispatch(setLoading(true))
    try {
      const response = await getPoolDataAPI(payload)
      const data = response.data.data
      const reformatPoolNumber = [...data].map((poolOrderResp) => {
        if (poolOrderResp.pool_number !== 0) {
          return {
            ...poolOrderResp,
            formatted_pool_number: poolOrderResp.orders[0].formatted_pool_number,
          }
        }
        return {
          ...poolOrderResp,
          formatted_pool_number: poolOrderResp.pool_number.toString(),
        }
      })
      dispatch(setMainPoolData(reformatPoolNumber))
      return data
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)
/**
 * @typedef {Object} TagPoolDataPayload
 * @property {string} order_invoice - The order invoice number
 * @property {string} pool_id - The pool ID
 */

/**
 * @param {TagPoolDataPayload} payload
 */
export const tagPoolData = createAsyncThunk(
  'orderPooling/tagPoolData',
  async (payload, { dispatch }) => {
    const { otelContext, ...requestPayload } = payload

    try {
      const response = await tagPoolDataAPI(requestPayload)
      return response
    } catch (error) {
      callErrorMsg(error)
      logPoolingApiError({
        action: TAG_POOL_ACTION,
        error,
        otelContext,
        payload: requestPayload,
      })
      return error
    }
  },
)

export const unTagPoolData = createAsyncThunk(
  'orderPooling/unTagPoolData',
  async (payload, { dispatch }) => {
    const { otelContext, ...requestPayload } = payload

    try {
      const response = await unTagPoolDataAPI(requestPayload)
      return response
    } catch (error) {
      callErrorMsg(error)
      logPoolingApiError({
        action: UNTAG_POOL_ACTION,
        error,
        otelContext,
        payload: requestPayload,
      })
    }
  },
)

export const getUnpoolReasons = createAsyncThunk(
  'orderPooling/getUnpoolReasons',
  async (payload, { dispatch }) => {
    try {
      const response = await fetchUnpoolReasonsAPI()
      return response
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchLockSequencePoolInfo = createAsyncThunk(
  'orderPooling/fetchLockSequencePoolInfo',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getLockSequencePoolingInfo(payload)
      return response.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const tagLockSequencePoolData = createAsyncThunk(
  'orderPooling/tagLockSequencePoolData',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await putLockSequenceTagPooling(payload)
      return response.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const untagLockSequencePoolData = createAsyncThunk(
  'orderPooling/untagLockSequencePoolData',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await putLockSequenceUntagPooling(payload)
      return response.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export default orderPooling.reducer
